export const ROLES = {
  ADMIN: 'admin',
  INITIATOR: 'initiator',
  SUPPLIER: 'supplier',
} as const;

export type Role = (typeof ROLES)[keyof typeof ROLES];

/**
 * The API's display role for the tenant-provisioning super user.
 *
 * It is NOT a fourth entry in ROLES: for authorization purposes a Mega Admin is
 * an admin (userMapper maps it to ROLES.ADMIN), so every existing route guard
 * and menu filter keeps working unchanged. What differs is the shell it gets --
 * a fixed Organization Setup sidebar and no dashboard -- which is a display
 * concern keyed off the raw role name rather than a new permission tier.
 */
export const MEGA_ADMIN_ROLE_NAME = 'mega admin';

/** Case- and whitespace-insensitive, because the name comes from the API. */
export function isMegaAdminRoleName(roleName: string | null | undefined): boolean {
  return roleName?.trim().toLowerCase() === MEGA_ADMIN_ROLE_NAME;
}

/**
 * The API's display role for a tenant administrator (`{ id: 9, name: "Admin" }`
 * on GET /users/me).
 *
 * Like MEGA_ADMIN_ROLE_NAME this is not a fourth entry in ROLES -- an Admin
 * maps to ROLES.ADMIN and keeps every existing route guard and menu filter.
 * What differs is the shell: an Admin works from the dashboard and the Masters,
 * and never touches Organization Setup, which belongs to the Mega Admin who
 * provisions the tenant.
 *
 * This predicate is the single place that knows the name, so nothing else has
 * to spell "Admin" again.
 */
export const ADMIN_ROLE_NAME = 'admin';

export function isAdminRoleName(roleName: string | null | undefined): boolean {
  return roleName?.trim().toLowerCase() === ADMIN_ROLE_NAME;
}

/**
 * The role a supplier's own sign-in carries. Suppliers are onboarded through Supplier Master, never made as
 * users in User Management, so the User Role dropdown does not offer it (2026-11-03).
 */
export const SUPPLIER_ROLE_NAME = 'supplier';

export function isSupplierRoleName(roleName: string | null | undefined): boolean {
  return roleName?.trim().toLowerCase() === SUPPLIER_ROLE_NAME;
}

export const ROLE_HIERARCHY: Record<string, number> = {
  [ROLES.ADMIN]: 100,
  [ROLES.INITIATOR]: 50,
  [ROLES.SUPPLIER]: 10,
};

export function hasMinimumRole(userRoles: readonly string[], requiredRole: string): boolean {
  const required = ROLE_HIERARCHY[requiredRole] ?? Number.POSITIVE_INFINITY;
  return userRoles.some((role) => (ROLE_HIERARCHY[role] ?? -1) >= required);
}
